/**
 * VoiceService - LiveKit integration for Midgard voice chat
 * 
 * Handles:
 * - Direct Talk voice between two players
 * - Social Zone voice for seated players
 * - Microphone permissions and device management
 * - Connection state management
 * 
 * IMPORTANT: This service is isolated from React Three Fiber rendering.
 * Voice state changes should NOT cause 3D world re-renders.
 */

import { Room, RoomEvent, Track, ConnectionState, LocalParticipant, RemoteParticipant } from 'livekit-client';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Voice connection states
export const VoiceState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// Voice room types
export const VoiceRoomType = {
  DIRECT: 'direct',
  DIRECT_TALK: 'direct', // Alias
  ZONE: 'zone',
  SOCIAL_ZONE: 'zone', // Alias
  PROXIMITY: 'proximity', // World/spatial voice
};

class VoiceService {
  constructor() {
    this.room = null;
    this.currentRoomType = null;
    this.currentRoomId = null;
    this.connectionState = VoiceState.DISCONNECTED;
    this.isMuted = true; // Start muted by default
    this.micPermissionGranted = false;
    
    // Event callbacks (set by UI components)
    this.onConnectionStateChange = null;
    this.onParticipantJoined = null;
    this.onParticipantLeft = null;
    this.onParticipantSpeaking = null;
    this.onMuteStateChange = null;
    this.onError = null;
    
    // Aliases for VoiceControls compatibility
    this.onStateChange = null;
    this.onMuteChange = null;
    this.onParticipantsChange = null;
    
    // Track participants
    this.participants = new Map();
  }
  
  /**
   * Check if voice service is available on this device
   */
  async checkAvailability() {
    try {
      const response = await fetch(`${API_URL}/api/voice/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('midgard_token')}`,
        },
      });
      
      if (!response.ok) {
        return { available: false, reason: 'Server error' };
      }
      
      const data = await response.json();
      
      if (!data.configured) {
        return { available: false, reason: 'Voice chat not configured on server' };
      }
      
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return { available: false, reason: 'Browser does not support microphone access' };
      }
      
      return { available: true };
    } catch (err) {
      console.error('Voice availability check failed:', err);
      return { available: false, reason: 'Failed to check voice availability' };
    }
  }
  
  /**
   * Request microphone permission
   */
  async requestMicrophonePermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed to check permission
      stream.getTracks().forEach(track => track.stop());
      this.micPermissionGranted = true;
      return { granted: true };
    } catch (err) {
      console.error('Microphone permission denied:', err);
      this.micPermissionGranted = false;
      return { 
        granted: false, 
        reason: err.name === 'NotAllowedError' 
          ? 'Microphone permission denied' 
          : 'Microphone not available'
      };
    }
  }
  
  /**
   * Join a direct voice conversation
   */
  async joinDirectVoice(conversationId, targetUserId) {
    if (this.room && this.connectionState === VoiceState.CONNECTED) {
      await this.leaveVoice();
    }
    
    return this._joinVoice(VoiceRoomType.DIRECT, {
      conversation_id: conversationId,
      target_user_id: targetUserId,
    }, '/api/voice/token/direct');
  }
  
  /**
   * Join a zone voice conversation
   */
  async joinZoneVoice(sceneId, zoneId) {
    if (this.room && this.connectionState === VoiceState.CONNECTED) {
      await this.leaveVoice();
    }
    
    return this._joinVoice(VoiceRoomType.ZONE, {
      scene_id: sceneId,
      zone_id: zoneId,
    }, '/api/voice/token/zone');
  }
  
  /**
   * Join proximity voice for open-world
   */
  async joinProximityVoice(worldId) {
    if (this.room && this.connectionState === VoiceState.CONNECTED) {
      await this.leaveVoice();
    }
    
    return this._joinVoice(VoiceRoomType.PROXIMITY, {
      world_id: worldId,
    }, '/api/voice/token/proximity');
  }
  
  /**
   * Internal join implementation
   */
  async _joinVoice(roomType, requestBody, tokenEndpoint) {
    this._setConnectionState(VoiceState.CONNECTING);
    
    try {
      // Get token from backend
      const token = localStorage.getItem('midgard_token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_URL}${tokenEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get voice token');
      }
      
      const { server_url, token: voiceToken, room: roomName } = await response.json();
      
      // Create and configure room
      this.room = new Room({
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        adaptiveStream: true,
        dynacast: true,
      });
      
      // Set up event listeners
      this._setupRoomListeners();
      
      // Connect to LiveKit
      await this.room.connect(server_url, voiceToken);
      
      // Store room info
      this.currentRoomType = roomType;
      this.currentRoomId = roomName;
      
      this._setConnectionState(VoiceState.CONNECTED);
      
      return { success: true };
    } catch (err) {
      console.error('Failed to join voice:', err);
      this._setConnectionState(VoiceState.ERROR);
      this._notifyError(err.message);
      return { success: false, error: err.message };
    }
  }
  
  /**
   * Leave current voice room
   */
  async leaveVoice() {
    if (!this.room) return;
    
    try {
      // Notify backend we're leaving
      const token = localStorage.getItem('midgard_token');
      if (token && this.currentRoomType && this.currentRoomId) {
        const endpoint = this.currentRoomType === VoiceRoomType.DIRECT 
          ? '/api/voice/leave/direct'
          : '/api/voice/leave/zone';
        
        // Fire and forget - don't await
        fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(
            this.currentRoomType === VoiceRoomType.DIRECT
              ? { conversation_id: this.currentRoomId, target_user_id: '' }
              : { scene_id: '', zone_id: this.currentRoomId }
          ),
        }).catch(() => {});
      }
      
      await this.room.disconnect();
    } catch (err) {
      console.error('Error leaving voice:', err);
    } finally {
      this.room = null;
      this.currentRoomType = null;
      this.currentRoomId = null;
      this.participants.clear();
      this._setConnectionState(VoiceState.DISCONNECTED);
    }
  }
  
  /**
   * Simplified join room method (used by VoiceControls)
   * @param {string} roomId - Room ID in format "zone:zone-id" or "direct:conversation-id" or "proximity:world-id"
   * @param {string} roomType - VoiceRoomType.ZONE, VoiceRoomType.DIRECT, or VoiceRoomType.PROXIMITY
   */
  async joinRoom(roomId, roomType) {
    // Extract the actual ID from the prefixed format
    const actualId = roomId.includes(':') ? roomId.split(':')[1] : roomId;
    
    if (roomType === VoiceRoomType.PROXIMITY || roomType === 'proximity') {
      return this.joinProximityVoice(actualId);
    } else if (roomType === VoiceRoomType.ZONE || roomType === 'zone') {
      return this.joinZoneVoice('', actualId);
    } else {
      return this.joinDirectVoice(actualId, '');
    }
  }
  
  /**
   * Leave room (alias for leaveVoice for VoiceControls compatibility)
   */
  async leaveRoom() {
    return this.leaveVoice();
  }
  
  /**
   * Toggle microphone mute state
   */
  async toggleMute() {
    if (!this.room || !this.room.localParticipant) return;
    
    try {
      const newMuteState = !this.isMuted;
      
      if (newMuteState) {
        // Mute - disable microphone track
        await this.room.localParticipant.setMicrophoneEnabled(false);
      } else {
        // Unmute - enable microphone track
        await this.room.localParticipant.setMicrophoneEnabled(true);
      }
      
      this.isMuted = newMuteState;
      this.onMuteStateChange?.(this.isMuted);
      this.onMuteChange?.(this.isMuted); // Alias for VoiceControls
      
      return this.isMuted;
    } catch (err) {
      console.error('Failed to toggle mute:', err);
      this._notifyError('Failed to toggle microphone');
      return this.isMuted;
    }
  }
  
  /**
   * Set mute state explicitly
   */
  async setMuted(muted) {
    if (this.isMuted === muted) return;
    return this.toggleMute();
  }
  
  /**
   * Get current participants
   */
  getParticipants() {
    return Array.from(this.participants.values());
  }
  
  /**
   * Set volume for a specific participant (used for spatial audio)
   * @param {string} participantId - The participant's identity
   * @param {number} volume - Volume level 0-1
   */
  setParticipantVolume(participantId, volume) {
    if (!this.room) return;
    
    const participant = this.room.remoteParticipants.get(participantId);
    if (!participant) return;
    
    // Set volume on all audio tracks from this participant
    participant.audioTrackPublications.forEach((pub) => {
      if (pub.track && pub.track.kind === 'audio') {
        // LiveKit allows setting volume 0-1 on audio elements
        const mediaStreamTrack = pub.track.mediaStreamTrack;
        if (mediaStreamTrack) {
          // Use the AudioContext gain node or direct volume control
          // For simplicity, we'll use the attached audio element if available
          pub.track.attachedElements?.forEach((el) => {
            if (el instanceof HTMLAudioElement) {
              el.volume = Math.max(0, Math.min(1, volume));
            }
          });
        }
      }
    });
  }
  
  /**
   * Set volumes for multiple participants at once (spatial audio batch update)
   * @param {Map<string, number>} volumeMap - Map of participantId -> volume
   */
  setParticipantVolumes(volumeMap) {
    for (const [participantId, volume] of volumeMap) {
      this.setParticipantVolume(participantId, volume);
    }
  }
  
  /**
   * Set up LiveKit room event listeners
   */
  _setupRoomListeners() {
    if (!this.room) return;
    
    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      switch (state) {
        case ConnectionState.Connected:
          this._setConnectionState(VoiceState.CONNECTED);
          break;
        case ConnectionState.Reconnecting:
          this._setConnectionState(VoiceState.RECONNECTING);
          break;
        case ConnectionState.Disconnected:
          this._setConnectionState(VoiceState.DISCONNECTED);
          break;
        default:
          break;
      }
    });
    
    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('[Voice] Participant connected:', participant.identity);
      this._addParticipant(participant);
      
      // Subscribe to their audio tracks
      participant.trackPublications.forEach((pub) => {
        if (pub.track && pub.kind === 'audio') {
          this._attachAudioTrack(pub.track);
        }
      });
    });
    
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('[Voice] Participant disconnected:', participant.identity);
      this._removeParticipant(participant);
    });
    
    // Handle when remote participant publishes a new track
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('[Voice] Track subscribed:', track.kind, 'from', participant.identity);
      if (track.kind === 'audio') {
        this._attachAudioTrack(track);
      }
    });
    
    // Handle when remote audio track is unsubscribed
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('[Voice] Track unsubscribed:', track.kind, 'from', participant.identity);
      if (track.kind === 'audio') {
        track.detach();
      }
    });
    
    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      const speakerIds = speakers.map(s => s.identity);
      this.onParticipantSpeaking?.(speakerIds);
    });
    
    this.room.on(RoomEvent.Disconnected, () => {
      this._setConnectionState(VoiceState.DISCONNECTED);
    });
    
    this.room.on(RoomEvent.MediaDevicesError, (err) => {
      console.error('Media device error:', err);
      this._notifyError('Microphone error: ' + err.message);
    });
    
    // Process existing participants
    this.room.remoteParticipants.forEach((participant) => {
      this._addParticipant(participant);
    });
  }
  
  _addParticipant(participant) {
    this.participants.set(participant.identity, {
      identity: participant.identity,
      name: participant.name || 'Player',
      isSpeaking: false,
    });
    this.onParticipantJoined?.(participant.identity, participant.name);
    this._notifyParticipantsChange();
  }
  
  _removeParticipant(participant) {
    this.participants.delete(participant.identity);
    this.onParticipantLeft?.(participant.identity);
    this._notifyParticipantsChange();
  }
  
  /**
   * Attach an audio track to a new audio element so it plays in the browser
   * This is CRITICAL - without this, remote audio won't be heard!
   */
  _attachAudioTrack(track) {
    if (!track) return;
    
    try {
      // Create an audio element and attach the track to it
      const audioElement = track.attach();
      
      // Ensure audio plays
      audioElement.volume = 1.0;
      audioElement.autoplay = true;
      
      // Append to DOM (required for some browsers)
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
      
      // Handle autoplay restrictions (especially on mobile)
      audioElement.play().catch((err) => {
        console.warn('[Voice] Audio autoplay blocked, waiting for user gesture:', err);
        // Set up one-time click handler to resume audio
        const resumeAudio = () => {
          audioElement.play().catch(() => {});
          document.removeEventListener('click', resumeAudio);
          document.removeEventListener('touchstart', resumeAudio);
        };
        document.addEventListener('click', resumeAudio, { once: true });
        document.addEventListener('touchstart', resumeAudio, { once: true });
      });
      
      console.log('[Voice] Audio track attached and playing');
    } catch (err) {
      console.error('[Voice] Failed to attach audio track:', err);
    }
  }
  
  _setConnectionState(state) {
    this.connectionState = state;
    this.onConnectionStateChange?.(state);
    this.onStateChange?.(state); // Alias for VoiceControls
  }
  
  _notifyError(message) {
    this.onError?.(message);
  }
  
  _notifyParticipantsChange() {
    this.onParticipantsChange?.(this.participants.size + 1); // +1 for local participant
  }
  
  /**
   * Clean up - call on unmount
   */
  async cleanup() {
    await this.leaveVoice();
    this.onConnectionStateChange = null;
    this.onParticipantJoined = null;
    this.onParticipantLeft = null;
    this.onParticipantSpeaking = null;
    this.onMuteStateChange = null;
    this.onStateChange = null;
    this.onMuteChange = null;
    this.onParticipantsChange = null;
    this.onError = null;
  }
}

// Global singleton instance
const voiceService = new VoiceService();
export default voiceService;
